import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListInventoryVendors,
  useListLedgerAccounts,
  useListCostCentres,
  useCreateCostCentre,
  getListCostCentresQueryKey,
  useCreateVendorBill,
  useUpdateVendorBill,
  getListVendorBillsQueryKey,
  getGetVendorBillQueryKey,
} from "@workspace/api-client-react";
import type { VendorBillDetail, TaxTreatment } from "@workspace/api-client-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { inr, today, round2 } from "@/lib/format";
import { venId } from "@/lib/display-id";
import { uploadInvoice } from "@/lib/upload";
import { FileField } from "@/components/expenses/file-field";
import { Plus, Trash2 } from "lucide-react";

// Phase 57 Plan 16, Task 1/2 — the bill editor sheet: header + a per-line
// (description, expense account, cost centre, taxable amount, GST rate)
// table, per <locked_ux>. Create-only for a brand-new bill; PATCH is
// draft-only server-side (vendor-bills.ts), so this sheet is only ever
// opened for `bill == null` (create) or a bill whose status is "draft".

export const GST_RATES = [0, 5, 12, 18, 28, 40] as const;

export const TAX_TREATMENTS: { value: TaxTreatment; label: string }[] = [
  { value: "registered", label: "Registered vendor (Input GST claimable)" },
  { value: "unregistered", label: "Unregistered vendor (no Input GST)" },
  { value: "rcm", label: "RCM applicable" },
  { value: "itc_blocked", label: "ITC blocked (§17(5), e.g. F&B)" },
];

interface LineForm {
  key: string;
  description: string;
  expenseAccountCode: string;
  costCentreId: number | null;
  taxableAmount: string;
  gstRate: number;
}

let lineKeySeq = 0;
function newLine(): LineForm {
  lineKeySeq += 1;
  return { key: `l${lineKeySeq}`, description: "", expenseAccountCode: "", costCentreId: null, taxableAmount: "", gstRate: 0 };
}

interface HeaderForm {
  vendorId: number | null;
  billNumber: string;
  billDate: string;
  dueDate: string;
  taxTreatment: TaxTreatment;
  isInterState: boolean;
  isPrepaid: boolean;
  prepaidMonths: string;
  prepaidStartMonth: string; // 'YYYY-MM'
  invoiceUrl: string | null;
  notes: string;
}

function emptyHeader(): HeaderForm {
  return {
    vendorId: null,
    billNumber: "",
    billDate: today(),
    dueDate: "",
    taxTreatment: "registered",
    isInterState: false,
    isPrepaid: false,
    prepaidMonths: "",
    prepaidStartMonth: today().slice(0, 7),
    invoiceUrl: null,
    notes: "",
  };
}

function headerFromBill(bill: VendorBillDetail): HeaderForm {
  return {
    vendorId: bill.vendorId,
    billNumber: bill.billNumber,
    billDate: bill.billDate.slice(0, 10),
    dueDate: bill.dueDate ? bill.dueDate.slice(0, 10) : "",
    taxTreatment: bill.taxTreatment,
    isInterState: bill.isInterState,
    isPrepaid: bill.isPrepaid,
    prepaidMonths: bill.prepaidMonths != null ? String(bill.prepaidMonths) : "",
    prepaidStartMonth: bill.prepaidStartMonth ? bill.prepaidStartMonth.slice(0, 7) : today().slice(0, 7),
    invoiceUrl: bill.invoiceUrl,
    notes: bill.notes ?? "",
  };
}

function linesFromBill(bill: VendorBillDetail): LineForm[] {
  if (bill.lines.length === 0) return [newLine()];
  return bill.lines.map((l) => {
    lineKeySeq += 1;
    return {
      key: `l${lineKeySeq}`,
      description: l.description,
      expenseAccountCode: l.expenseAccountCode,
      costCentreId: l.costCentreId,
      taxableAmount: String(l.taxableAmount),
      gstRate: l.gstRate,
    };
  });
}

export interface BillEditorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null = creating a brand-new one-off bill. Non-null must be a draft
   * (or the just-created bill this sheet itself produced) — PATCH is
   * draft-only server-side. */
  bill: VendorBillDetail | null;
}

export function BillEditorSheet({ open, onOpenChange, bill }: BillEditorSheetProps) {
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data: vendors } = useListInventoryVendors({});
  const { data: accounts } = useListLedgerAccounts({ type: "expense", active: true });
  const { data: costCentres } = useListCostCentres();
  const createCostCentre = useCreateCostCentre();
  const createMut = useCreateVendorBill();
  const updateMut = useUpdateVendorBill();

  const [header, setHeader] = useState<HeaderForm>(emptyHeader());
  const [lines, setLines] = useState<LineForm[]>([newLine()]);
  // A3 — only ever set from a CREATE response's `isInterStateInferred` (the
  // one field 57-09's OpenAPI contract doesn't carry — see vendor-bills.ts's
  // POST handler). An existing/edited bill's isInterState is always already
  // explicit, so this stays false outside a fresh create.
  const [interStateInferred, setInterStateInferred] = useState(false);
  // Once a create succeeds, further edits in the SAME sheet session are
  // PATCHes against the new row — this is what "review and correct the
  // inferred inter-state flag, then Submit" looks like without closing.
  const [createdBill, setCreatedBill] = useState<VendorBillDetail | null>(null);
  const [ccDialogFor, setCcDialogFor] = useState<string | null>(null);
  const [ccForm, setCcForm] = useState({ code: "", name: "" });

  const activeBill = bill ?? createdBill;
  const isCreateMode = activeBill == null;

  useEffect(() => {
    if (!open) return;
    setCreatedBill(null);
    setInterStateInferred(false);
    if (bill) {
      setHeader(headerFromBill(bill));
      setLines(linesFromBill(bill));
    } else {
      setHeader(emptyHeader());
      setLines([newLine()]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bill?.id]);

  function updateLine(key: string, patch: Partial<LineForm>) {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }
  function removeLine(key: string) {
    setLines((prev) => prev.filter((l) => l.key !== key));
  }
  function addLine() {
    setLines((prev) => [...prev, newLine()]);
  }

  const totals = useMemo(() => {
    let taxable = 0;
    let gst = 0;
    for (const l of lines) {
      const t = round2(Number(l.taxableAmount) || 0);
      const g = round2((t * l.gstRate) / 100);
      taxable = round2(taxable + t);
      gst = round2(gst + g);
    }
    return { taxable, gst, gross: round2(taxable + gst) };
  }, [lines]);

  const canSave = lines.length > 0 && header.vendorId != null && header.billNumber.trim() !== "" && !!header.billDate;
  const saving = createMut.isPending || updateMut.isPending;

  async function openNewCostCentre(lineKey: string) {
    setCcDialogFor(lineKey);
    setCcForm({ code: "", name: "" });
  }

  async function saveNewCostCentre() {
    if (ccDialogFor == null) return;
    if (!ccForm.code.trim() || !ccForm.name.trim()) {
      toast({ title: "Code and name are both required.", variant: "destructive" });
      return;
    }
    try {
      const row = await createCostCentre.mutateAsync({ data: { code: ccForm.code.trim(), name: ccForm.name.trim() } });
      qc.invalidateQueries({ queryKey: getListCostCentresQueryKey() });
      updateLine(ccDialogFor, { costCentreId: row.id });
      toast({ title: `Cost centre "${row.name}" created and selected on this line.` });
      setCcDialogFor(null);
    } catch {
      toast({ title: "Could not create cost centre — code may already exist.", variant: "destructive" });
    }
  }

  async function handleSave() {
    if (!canSave) return;
    for (const l of lines) {
      if (!l.expenseAccountCode || l.costCentreId == null) {
        toast({ title: "Every line needs an expense account and a cost centre.", variant: "destructive" });
        return;
      }
      if (!(Number(l.taxableAmount) >= 0)) {
        toast({ title: "Every line needs a taxable amount >= 0.", variant: "destructive" });
        return;
      }
    }
    if (header.isPrepaid && (Number(header.prepaidMonths) < 2 || !header.prepaidStartMonth)) {
      toast({ title: "Prepaid requires at least 2 months and a start month.", variant: "destructive" });
      return;
    }

    const lineBodies = lines.map((l) => ({
      description: l.description.trim() || "(no description)",
      expenseAccountCode: l.expenseAccountCode,
      costCentreId: l.costCentreId!,
      taxableAmount: round2(Number(l.taxableAmount) || 0),
      gstRate: l.gstRate,
    }));

    try {
      if (isCreateMode) {
        const result = await createMut.mutateAsync({
          data: {
            vendorId: header.vendorId!,
            billNumber: header.billNumber.trim(),
            billDate: header.billDate,
            dueDate: header.dueDate || null,
            taxTreatment: header.taxTreatment,
            isPrepaid: header.isPrepaid,
            prepaidMonths: header.isPrepaid ? Number(header.prepaidMonths) : null,
            prepaidStartMonth: header.isPrepaid ? `${header.prepaidStartMonth}-01` : null,
            invoiceUrl: header.invoiceUrl,
            notes: header.notes || null,
            lines: lineBodies,
          },
        });
        // A3 — `isInterStateInferred` is real on the wire (vendor-bills.ts's
        // POST handler) but not yet in 57-09's generated OpenAPI contract for
        // this endpoint (flagged in 57-13/57-16's own notes) — cast, don't
        // hand-write a second fetch.
        const withInferred = result as VendorBillDetail & { isInterStateInferred?: boolean };
        setCreatedBill(result);
        setInterStateInferred(!!withInferred.isInterStateInferred);
        setHeader((h) => ({ ...h, isInterState: result.isInterState }));
        qc.invalidateQueries({ queryKey: getListVendorBillsQueryKey() });
        toast({ title: `Bill #${result.id} created as a draft.` });
      } else {
        await updateMut.mutateAsync({
          id: activeBill!.id,
          data: {
            billNumber: header.billNumber.trim(),
            billDate: header.billDate,
            dueDate: header.dueDate || null,
            taxTreatment: header.taxTreatment,
            isInterState: header.isInterState,
            isPrepaid: header.isPrepaid,
            prepaidMonths: header.isPrepaid ? Number(header.prepaidMonths) : null,
            prepaidStartMonth: header.isPrepaid ? `${header.prepaidStartMonth}-01` : null,
            invoiceUrl: header.invoiceUrl,
            notes: header.notes || null,
            lines: lineBodies,
          },
        });
        qc.invalidateQueries({ queryKey: getListVendorBillsQueryKey() });
        qc.invalidateQueries({ queryKey: getGetVendorBillQueryKey(activeBill!.id) });
        toast({ title: "Bill updated." });
        onOpenChange(false);
      }
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Something went wrong.", variant: "destructive" });
    }
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-[720px] flex flex-col overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{isCreateMode ? "New Bill" : `Edit Bill #${activeBill!.id}`}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold">
                  Vendor <span className="text-destructive">*</span>
                </label>
                <Select
                  value={header.vendorId != null ? String(header.vendorId) : ""}
                  onValueChange={(v) => setHeader((h) => ({ ...h, vendorId: Number(v) }))}
                  disabled={!isCreateMode}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {(vendors ?? []).map((v) => (
                      <SelectItem key={v.id} value={String(v.id)}>
                        {venId(v.id)} · {v.vendorName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold">
                  Bill number <span className="text-destructive">*</span>
                </label>
                <Input
                  placeholder="Vendor's own invoice number"
                  value={header.billNumber}
                  onChange={(e) => setHeader((h) => ({ ...h, billNumber: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold">
                  Bill date <span className="text-destructive">*</span>
                </label>
                <Input type="date" value={header.billDate} onChange={(e) => setHeader((h) => ({ ...h, billDate: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-semibold">Due date</label>
                <Input type="date" value={header.dueDate} onChange={(e) => setHeader((h) => ({ ...h, dueDate: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold">Tax treatment</label>
              {/* Native <select> — a real Radix Select popover lives in a
                  portal, which is needlessly brittle to drive from this
                  file's own component test; a plain enum picker gains
                  nothing from the styled version. */}
              <select
                data-testid="tax-treatment"
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                value={header.taxTreatment}
                onChange={(e) => setHeader((h) => ({ ...h, taxTreatment: e.target.value as TaxTreatment }))}
              >
                {TAX_TREATMENTS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              {header.taxTreatment === "itc_blocked" && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  GST is not claimable on this purchase (§17(5)); the full GST-inclusive amount is charged to expense.
                </p>
              )}
            </div>

            <div className="flex items-center justify-between rounded-md border px-3 py-2">
              <div>
                <p className="text-xs font-semibold">Inter-state purchase</p>
                {interStateInferred ? (
                  <p className="text-[11px] text-muted-foreground">
                    Inferred from vendor state — please confirm.
                  </p>
                ) : (
                  <p className="text-[11px] text-muted-foreground">
                    Flat GST stays as-is this phase; this flag only captures the CGST/SGST-vs-IGST signal for later.
                  </p>
                )}
              </div>
              <Switch
                checked={header.isInterState}
                onCheckedChange={(v) => {
                  setHeader((h) => ({ ...h, isInterState: v }));
                  setInterStateInferred(false);
                }}
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={header.isPrepaid} onCheckedChange={(v) => setHeader((h) => ({ ...h, isPrepaid: v }))} />
              <label className="text-xs font-semibold">Prepaid — recognise over multiple months</label>
            </div>
            {header.isPrepaid && (
              <div className="grid grid-cols-2 gap-3 pl-6">
                <div>
                  <label className="text-xs font-semibold">Months (min 2)</label>
                  <Input
                    type="number"
                    min={2}
                    value={header.prepaidMonths}
                    onChange={(e) => setHeader((h) => ({ ...h, prepaidMonths: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold">Starting month</label>
                  <Input
                    type="month"
                    value={header.prepaidStartMonth}
                    onChange={(e) => setHeader((h) => ({ ...h, prepaidStartMonth: e.target.value }))}
                  />
                </div>
              </div>
            )}

            <div>
              <label className="text-xs font-semibold">Invoice</label>
              <FileField value={header.invoiceUrl} onChange={(u) => setHeader((h) => ({ ...h, invoiceUrl: u }))} uploadFn={uploadInvoice} label="Invoice" />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold">Lines</label>
                <Button type="button" size="sm" variant="outline" data-testid="add-line" onClick={addLine}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add line
                </Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead>Expense account</TableHead>
                    <TableHead>Cost centre</TableHead>
                    <TableHead className="text-right">Taxable ₹</TableHead>
                    <TableHead className="text-right">GST %</TableHead>
                    <TableHead className="text-right">GST ₹</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((l, idx) => {
                    const lineGst = round2(((Number(l.taxableAmount) || 0) * l.gstRate) / 100);
                    return (
                      <TableRow key={l.key}>
                        <TableCell>
                          <Input value={l.description} onChange={(e) => updateLine(l.key, { description: e.target.value })} placeholder="Line description" />
                        </TableCell>
                        <TableCell>
                          <Select value={l.expenseAccountCode || ""} onValueChange={(v) => updateLine(l.key, { expenseAccountCode: v })}>
                            <SelectTrigger className="w-[180px]">
                              <SelectValue placeholder="Account" />
                            </SelectTrigger>
                            <SelectContent>
                              {(accounts ?? []).map((a) => (
                                <SelectItem key={a.id} value={a.code}>
                                  {a.code} · {a.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Select
                              value={l.costCentreId != null ? String(l.costCentreId) : ""}
                              onValueChange={(v) => updateLine(l.key, { costCentreId: Number(v) })}
                            >
                              <SelectTrigger className="w-[150px]">
                                <SelectValue placeholder="Cost centre" />
                              </SelectTrigger>
                              <SelectContent>
                                {(costCentres ?? [])
                                  .filter((c) => c.active)
                                  .map((c) => (
                                    <SelectItem key={c.id} value={String(c.id)}>
                                      {c.code} · {c.name}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                            <Button type="button" size="icon" variant="ghost" title="New cost centre" onClick={() => openNewCostCentre(l.key)}>
                              <Plus className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            step="0.01"
                            className="text-right"
                            data-testid={`line-taxable-${idx}`}
                            value={l.taxableAmount}
                            onChange={(e) => updateLine(l.key, { taxableAmount: e.target.value })}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          {/* Native <select>, same testability reasoning as
                              the header's tax-treatment picker above. */}
                          <select
                            data-testid={`line-gst-${idx}`}
                            className="flex h-9 w-[80px] rounded-md border border-input bg-background px-2 py-1 text-sm shadow-sm ml-auto"
                            value={String(l.gstRate)}
                            onChange={(e) => updateLine(l.key, { gstRate: Number(e.target.value) })}
                          >
                            {GST_RATES.map((r) => (
                              <option key={r} value={String(r)}>
                                {r}%
                              </option>
                            ))}
                          </select>
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums">{inr(lineGst)}</TableCell>
                        <TableCell>
                          <Button type="button" size="icon" variant="ghost" data-testid={`line-remove-${idx}`} onClick={() => removeLine(l.key)}>
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {lines.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="h-16 text-center text-muted-foreground">
                        No lines — a bill needs at least one.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <div className="flex justify-end gap-6 text-sm font-mono tabular-nums pr-4">
                <span data-testid="totals-taxable">Taxable {inr(totals.taxable)}</span>
                <span data-testid="totals-gst">GST {inr(totals.gst)}</span>
                <span data-testid="totals-gross" className="font-semibold">
                  Gross {inr(totals.gross)}
                </span>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold">Notes</label>
              <Textarea value={header.notes} onChange={(e) => setHeader((h) => ({ ...h, notes: e.target.value }))} />
            </div>
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {isCreateMode && createdBill ? "Done" : "Cancel"}
            </Button>
            <Button data-testid="editor-save" onClick={handleSave} disabled={!canSave || saving}>
              {saving ? "Saving…" : isCreateMode ? (createdBill ? "Save changes" : "Create") : "Save changes"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Dialog open={ccDialogFor != null} onOpenChange={(o) => !o && setCcDialogFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>New cost centre</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold">Code</label>
              <Input value={ccForm.code} onChange={(e) => setCcForm((f) => ({ ...f, code: e.target.value }))} placeholder="e.g. FRONT_DESK" />
            </div>
            <div>
              <label className="text-xs font-semibold">Name</label>
              <Input value={ccForm.name} onChange={(e) => setCcForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Front Desk" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCcDialogFor(null)}>
              Cancel
            </Button>
            <Button onClick={saveNewCostCentre} disabled={createCostCentre.isPending}>
              {createCostCentre.isPending ? "Creating…" : "Create & select"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
